import { useCallback, useEffect, useState } from "react";
import { adminMigrationQueueApi } from "@/api/adminMigrationQueue";
import { releasesApi } from "@/api/releases";
import type { QueueItem, QueueState } from "@/api/migrationQueue";
import { sharedPoll, stopPoll } from "@/command/poll";
import { WorkflowOwnerControl } from "@/components/WorkflowOwnerControl";
import { QueueHistoryLinks } from "./QueueHistoryLinks";
import {
  isQueueHistory,
  prepareQueueSearch,
  queueResultCount,
  queueStatusLine,
  searchQueueItems,
} from "@/lib/queueSearch";
export function AdminDbQueueSection(
  { owner = false, searchQuery, onSearchChange }: {
    owner?: boolean;
    searchQuery?: string;
    onSearchChange?: (query: string) => void;
  },
) {
  const readOnly = adminMigrationQueueApi.readOnly;
  const [localSearch, setLocalSearch] = useState(""),
    query = searchQuery ?? localSearch,
    setQuery = onSearchChange ?? setLocalSearch;
  const [state, setState] = useState<QueueState | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  const [sql, setSql] = useState<{ id: string; text: string } | null>(null);
  const load = useCallback(async () => {
    try {
      const next = await adminMigrationQueueApi.state();
      setState({
        ...next,
        items: await Promise.all(next.items.map(prepareQueueSearch)),
      });
      setError("");
      return true;
    } catch {
      setState(null);
      setError("תור Admin אינו זמין. בדקו הרשאות והתקנת התור.");
      return false;
    }
  }, []);
  useEffect(() => {
    const t = sharedPoll(load, 30_000, true);
    return () => stopPoll(t);
  }, [load]);
  async function act(
    id: string | null,
    action:
      | "approve"
      | "hold"
      | "release"
      | "retry"
      | "cancel"
      | "pause"
      | "resume",
    name: string,
  ) {
    if (!owner || readOnly) return;
    if (!window.confirm(`פעולה בתור Admin: ${name}?`)) return;
    const reason = window.prompt("סיבה לפעולה");
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await adminMigrationQueueApi.action(id, action, reason.trim());
      await load();
    } catch {
      setError("הפעולה לא הושלמה. רעננו ובדקו את מצב התור.");
    } finally {
      setBusy(false);
    }
  }
  async function viewSql(item: QueueItem) {
    setBusy(true);
    setError("");
    setSql(null);
    try {
      const text = (await releasesApi.centerSql("admin", item.id)).sql;
      if (typeof text !== "string") throw Error("SQL unavailable");
      const actual = await prepareQueueSearch({
        ...item,
        sql: text,
        sql_sha256: undefined,
      });
      if (item.sql_sha256 && item.sql_sha256 !== actual.sql_sha256) {
        throw Error("SQL changed");
      }
      setSql({ id: item.id, text });
    } catch {
      setError(
        "תוכן SQL אינו זמין בקריאה המאומתת. ניתן לפתוח את הקובץ מהגרסה שבתור, אם קיים קישור.",
      );
    } finally {
      setBusy(false);
    }
  }
  const items = searchQueueItems(state?.items ?? [], query);
  return (
    <section
      aria-label="תור מיגרציות Admin"
      dir="rtl"
      className="mb-5 rounded-xl border border-navy-border bg-navy-card p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-semibold">תור שינויי בסיס נתונים — Admin</h2>
        <button
          disabled={busy}
          onClick={() => void load()}
          className="min-h-11 rounded-lg px-3 text-indigo-200"
        >
          רענון
        </button>
      </div>
      <p className="mt-2 text-sm text-ink-secondary">
        מסד Admin · אישור חירום מבוצע בידי ניצן בלבד. פריטים רגילים מוחלים בחלון
        הלילה אחרי מיזוג.
      </p>
      <label className="mt-3 block">
        חיפוש שינויי DB — Admin<input
          type="search"
          aria-label="חיפוש שינויי DB — Admin"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="גרסה, שם קובץ או חתימת תוכן…"
          className="mt-1 min-h-11 w-full rounded border border-gray-600 bg-gray-900 px-3"
        />
      </label>
      {error && <p role="alert" className="mt-3 text-rose-300">{error}</p>}
      {!state && !error && (
        <p role="status" className="mt-3">טוען את תור Admin…</p>
      )}
      {state && (
        <>
          <p className="mt-3 text-sm">
            {state.paused ? "הרץ מושהה" : "הרץ פעיל"} ·{" "}
            {state.guard_on ? "מחסום DDL פעיל" : "מחסום DDL כבוי"} ·{" "}
            {state.window_open ? "חלון לילה פתוח" : "מחוץ לחלון הלילה"}
          </p>
          <button
            disabled={busy || !owner || readOnly}
            onClick={() =>
              void act(
                null,
                state.paused ? "resume" : "pause",
                state.paused ? "חידוש הרץ" : "השהיית הרץ",
              )}
            className="mt-2 min-h-11 rounded border border-gray-600 px-3"
          >
            {state.paused ? "חידוש הרץ" : "השהיית הרץ"}
          </button>
          <p role="status" className="mt-2 text-sm">
            {queueResultCount(items)}
          </p>
          {!items.length && (
            <p className="mt-3 text-sm text-ink-secondary">
              אין שינויי DB שמתאימים לחיפוש בכל המצבים.
            </p>
          )}
          <div className="mt-3 space-y-3">
            {items.map((item) => (
              <details
                key={item.id}
                onToggle={(event) => {
                  if (event.currentTarget.open) void viewSql(item);
                }}
                className="rounded-lg border border-gray-700 p-3"
              >
                <summary className="min-h-11 cursor-pointer">
                  <span className="break-words font-medium">
                    <bdi>{item.name}</bdi>
                  </span>
                  <span className="mt-1 block text-xs text-gray-400">
                    <bdi>{item.version}</bdi> · {queueStatusLine(item)} ·{" "}
                    {item.emergency ? "חירום" : "רגיל"}
                    {item.held ? " · מוחזק" : ""}
                  </span>
                </summary>
                <QueueHistoryLinks item={item} />
                {item.sql_sha256 && (
                  <code className="block break-all" dir="ltr">
                    {item.sql_sha256}
                  </code>
                )}
                {item.last_error && (
                  <p className="mt-2 text-sm text-rose-300">
                    {item.last_error}
                  </p>
                )}
                {/^[a-f0-9]{40}$/i.test(item.git_sha ?? "") &&
                  item.file_path?.startsWith("supabase/migrations/") && (
                  <a
                    className="mt-2 block text-indigo-200 underline"
                    href={`https://github.com/the-Otomator/otomator-admin/blob/${item.git_sha}/${
                      item.file_path.split("/").map(encodeURIComponent).join(
                        "/",
                      )
                    }`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    לצפות בקובץ SQL מהגרסה שבתור
                  </a>
                )}
                <button
                  disabled={busy}
                  onClick={() => void viewSql(item)}
                  className="mt-2 min-h-11 rounded border border-gray-600 px-3"
                >
                  הצגת SQL
                </button>
                {sql?.id === item.id && (
                  <pre
                    dir="ltr"
                    className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap break-words text-xs"
                  >{sql.text}</pre>
                )}
                {!isQueueHistory(item) && (
                  <>
                    {!item.pr_url && !item.workflow_approval_id &&
                      ["pending", "approved"].includes(item.status) && (
                      <WorkflowOwnerControl
                        key={`${item.id}:${item.sql_sha256}`}
                        owner={owner}
                        sql={{
                          database: "admin",
                          item_id: item.id,
                          sha256: item.sql_sha256 ?? "",
                          name: item.name,
                        }}
                      />
                    )}
                    {item.status === "failed" && (
                      <button
                        disabled={busy || !owner || readOnly}
                        onClick={() => void act(item.id, "retry", item.name)}
                        className="mt-2 min-h-11 rounded border border-amber-500 px-3"
                      >
                        ניסיון חוזר — ניצן
                      </button>
                    )}
                    {["pending", "approved"].includes(item.status) && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {item.emergency && item.status === "pending" && (
                          <button
                            disabled={busy || !owner || readOnly}
                            onClick={() =>
                              void act(item.id, "approve", item.name)}
                            className="min-h-11 rounded bg-amber-500/20 px-3"
                          >
                            אישור חירום — ניצן
                          </button>
                        )}
                        <button
                          disabled={busy || !owner || readOnly}
                          onClick={() =>
                            void act(
                              item.id,
                              item.held ? "release" : "hold",
                              item.name,
                            )}
                          className="min-h-11 rounded border border-gray-600 px-3"
                        >
                          {item.held ? "שחרור החזקה" : "החזקה"}
                        </button>
                        <button
                          disabled={busy || !owner || readOnly}
                          onClick={() => void act(item.id, "cancel", item.name)}
                          className="min-h-11 rounded border border-gray-600 px-3"
                        >
                          ביטול
                        </button>
                      </div>
                    )}
                  </>
                )}
              </details>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
